// The 12-month trim (B12; the privacy promise in docs/PURPOSE.md): answers older than a year are
// folded into one `cards.compacted` event per person: each card's spaced-repetition state at the
// cutoff and the XP those answers earned. Nothing SRS needs is lost; the answer-by-answer detail
// is. The snapshot is authoritative at its cutoff, so a phone that still holds the old answers
// gets the same progress as one that doesn't (domain/arcade/progress.ts).
import { z } from 'zod'
import { review, type CardState, type Result } from './srs'

export const TRIM_AFTER_DAYS = 365

const State = z.object({ box: z.number().int(), due: z.string(), seen: z.number().int(), right: z.number().int(), lastDay: z.string() })
export const CardsCompacted = z.object({
  /** Answers before this instant (epoch ms) are folded in. */
  before: z.number().int(),
  cards: z.record(z.string(), State),
  /** The XP all folded answers earned (the person's answer XP up to `before`). */
  answeredXp: z.number(),
})
export type CardsCompacted = z.infer<typeof CardsCompacted>

interface Answer {
  at: number
  id: string
  payload: { cardId?: string; result?: Result; xp?: number; day?: string } | null
}

/** Folds answers (and an earlier snapshot) into a new snapshot at `before`. */
export function compact(answers: readonly Answer[], before: number, previous?: CardsCompacted): CardsCompacted {
  const cards: Record<string, CardState> = { ...(previous?.cards ?? {}) }
  let answeredXp = previous?.answeredXp ?? 0
  for (const a of [...answers].sort((x, y) => x.at - y.at || (x.id < y.id ? -1 : 1))) {
    const p = a.payload
    if (!p?.cardId || !p.result || !p.day) continue
    cards[p.cardId] = review(cards[p.cardId], p.result, p.day)
    answeredXp += p.xp ?? 0
  }
  return { before, cards, answeredXp }
}
